import { useEffect } from "react";
import VideoArea from "./videoArea";
import Image from "next/image";
import { checkCanComment, handleVideoElementSizeInfo } from "redux-share/actions";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { conditions, isTouchScreen, handleHasXTokenParamOrSendXToken, sidebarWidth, videoTag } from "helpers";
import { ConditionName } from "interfaces";
import { setAutoPlayPopover, setBookmarksPopover } from "redux-share/slices/popoversSlice";
import { setShowScreen } from "redux-share/slices/videoDataSlice";
import useFullScreen from "helpers/hooks/useFullScreen";
import Popovers from "ui/src/components/player-components/popover/popovers";
import IMAGES from "images/importImages";
import { shallowEqual } from "react-redux";
import dynamic from "next/dynamic";
import ChatRules from "../../../../player-components/chat/chatRules";
import { API, apiCaller } from "services";
import { setCommentDefaultMessages } from "redux-share/slices/commentsSlice";

const ComplexDesktop = dynamic(() => import("ui/src/components/player-components/complexDesktop/complexDesktop"));
const ComplexPortrait = dynamic(() => import("ui/src/components/player-components/complexPortrait/complexPortrait"));
const Skeleton = dynamic(() => import("../../../../player-components/skeleton/skeleton"));
const Chat = dynamic(() => import("ui/src/components/player-components/chat/chat"));
const RecommendationList = dynamic(() => import("ui/src/components/player-components/recommendationList/recommendationList"));
const PlayList = dynamic(() => import("ui/src/components/player-components/playList/playList"));
const BookmarkIcon = dynamic(() => import("ui/src/components/icon-components/bookmarkIcon"));

const VideoPlayer = () => {
    const dispatch = useAppDispatch();
    const { details, params, videoElementSizeInfo, showScreen, playList, recommendationList } = useAppSelector(
        ({
            videoData: { details, params, videoElementSizeInfo, showScreen, playList, recommendationList, isComplexDesktop, isComplexPortrait }
        }) => ({
            details,
            params,
            videoElementSizeInfo,
            showScreen,
            playList,
            recommendationList,
            isComplexDesktop,
            isComplexPortrait
        }),
        shallowEqual
    );
    const { firstPlay, showMiniPlayer, isFullScreen, isEnded, isAutoPlay, loadedFragmentsCount } = useAppSelector(
        ({ controller: { firstPlay, showMiniPlayer, isFullScreen, isEnded, isAutoPlay, loadedFragmentsCount } }) => ({
            firstPlay,
            showMiniPlayer,
            isFullScreen,
            isEnded,
            isAutoPlay,
            loadedFragmentsCount
        }),
        shallowEqual
    );
    const { avatar_url, name, phone_number } = useAppSelector(
        ({ user: { settings, avatar_url, name, phone_number, isLogin } }) => ({ settings, avatar_url, name, phone_number, isLogin }),
        shallowEqual
    );
    useAppSelector(({ popovers: { chatPopover } }) => ({ chatPopover }), shallowEqual);
    useAppSelector(({ controller: { loadedFragmentsCount } }) => ({ loadedFragmentsCount }), shallowEqual);

    useEffect(() => {
        if (conditions(ConditionName.hasPermittedLoadedFragment)) {
            if (conditions(ConditionName.isParamsPortrait)) {
                (async () => {
                    const response = await apiCaller({
                        method: "GET",
                        url: API().comment.default_message(details.user_id!),
                        headers: handleHasXTokenParamOrSendXToken()
                    });
                    if (response) dispatch(setCommentDefaultMessages(response.data.results));
                })();
                checkCanComment();
            }
            const videoPlayerModule: any = document.getElementById("video-player-module");
            if (conditions(ConditionName.isDarkMode)) videoPlayerModule.classList.add("pl-bg-dark-1");
        }
    }, [videoTag(), loadedFragmentsCount.length]);

    useEffect(() => {
        if (conditions(ConditionName.isEnded) && conditions(ConditionName.isParamsPortrait)) dispatch(setAutoPlayPopover(true));
    }, [isAutoPlay, isEnded]);

    useEffect(() => {
        if (videoTag()) {
            let timeout: any;
            const handleResize = () => {
                if (!conditions(ConditionName.showMiniPlayer)) {
                    timeout = setTimeout(
                        () => {
                            handleVideoElementSizeInfo();
                            dispatch(setShowScreen(true));
                        },
                        isTouchScreen() ? 0 : 300
                    );
                }
            };
            handleResize();
            window.addEventListener("resize", handleResize);
            const appScroll = document.getElementsByClassName("app-scroll")[0];
            if (appScroll) appScroll.addEventListener("scroll", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (appScroll) appScroll.removeEventListener("scroll", handleResize);
                clearTimeout(timeout);
            };
        }
    }, [firstPlay, recommendationList, playList, showMiniPlayer, isFullScreen, videoTag()]);

    useFullScreen();

    return (
        <>
            <div
                className={`pl-transition-opacity pl-duration-500 ${showScreen ? "pl-opacity-100" : "pl-opacity-0"} ${
                    conditions(ConditionName.backgroundColorInLightMode) ? "pl-bg-white" : "pl-bg-dark-1"
                } dark:pl-bg-dark-1 ${!!params.width ? params.width : "pl-max-w-screen"} ${
                    conditions(ConditionName.heightScreen) ? "dvh" : params.height
                } pl-flex pl-items-start`}
            >
                <div
                    id="parent-player"
                    className={`pl-w-full pl-h-full ${conditions(ConditionName.hasSidebar) && "pl-ml-[10px]"} ${
                        conditions(ConditionName.isComplexPortrait) && "pl-bg-dark-1"
                    }`}
                >
                    <VideoArea />
                    {conditions(ConditionName.isComplexPortrait) && <ComplexPortrait />}
                    {conditions(ConditionName.isComplexDesktop) && <ComplexDesktop />}
                </div>
                {conditions(ConditionName.isBeforeRequest_Playlist_Chat_Recommendation) && conditions(ConditionName.isComplexDesktop) ? (
                    <div className="pl-flex-none" style={{ width: sidebarWidth }}>
                        <div
                            className="pl-flex pl-flex-col"
                            style={{
                                maxHeight: videoElementSizeInfo.height,
                                height: conditions(ConditionName.showChat) ? videoElementSizeInfo.height : "unset"
                            }}
                        >
                            <div
                                className={`pl-flex pl-flex-col pl-gap-2 pl-mx-3 ${
                                    conditions(ConditionName.isComplexPortrait) && `pl-mb-[300px] pl-mx-3`
                                }`}
                            >
                                {Array.from(Array(7).keys()).map((item, index) => (
                                    <Skeleton key={index} />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    conditions(ConditionName.hasSidebar) && (
                        <div className="pl-flex-none" style={{ width: sidebarWidth }}>
                            <div
                                className="pl-flex pl-flex-col"
                                style={{
                                    maxHeight: videoElementSizeInfo.height,
                                    height: conditions(ConditionName.showChat) ? videoElementSizeInfo.height : "unset"
                                }}
                            >
                                {conditions(ConditionName.isLogin) && (
                                    <div className="pl-text-center pl-border pl-border-secondary-400 pl-rounded-[6px] pl-ml-4 pl-py-3 pl-flex pl-flex-col pl-gap-2 pl-mb-4">
                                        <div className="pl-flex pl-flex-col pl-gap-2 pl-border-b pl-border-secondary-400">
                                            <div className="pl-flex pl-justify-center">
                                                <Image
                                                    alt="avatar"
                                                    src={
                                                        conditions(ConditionName.hasUserAvatar)
                                                            ? avatar_url
                                                            : conditions(ConditionName.isDarkMode)
                                                            ? IMAGES.user1Black
                                                            : IMAGES.user1White
                                                    }
                                                    width={56}
                                                    height={56}
                                                    className="pl-rounded-full"
                                                />
                                            </div>
                                            {conditions(ConditionName.hasUserName) && (
                                                <p className="pl-text-secondary-600 dark:pl-text-secondary-100 pl-text-[16px] pl-font-bold">{name}</p>
                                            )}
                                            {conditions(ConditionName.hasUserPhoneNumber) && (
                                                <p style={{ direction: "ltr" }} className="pl-text-secondary-400 pl-text-[14px] pl-font-medium">
                                                    {phone_number}
                                                </p>
                                            )}
                                        </div>
                                        <div
                                            className="pl-flex pl-items-center pl-gap-3 pl-text-main dark:pl-text-main-highLight pl-border-[1.5px] pl-border-main dark:pl-border-main-highLight pl-rounded-[8px] pl-w-max pl-px-4 pl-py-2 pl-mx-auto pl-cursor-pointer"
                                            onClick={() => dispatch(setBookmarksPopover(true))}
                                        >
                                            <BookmarkIcon className="pl-w-4 pl-h-4 pl-fill-main dark:pl-fill-main-highLight" isBookmark={false} />
                                            <p className="pl-text-[15px] pl-font-medium">لیست ذخیره‌شده‌ها</p>
                                        </div>
                                    </div>
                                )}
                                {conditions(ConditionName.chatEnabled) && conditions(ConditionName.show_Chat) ? (
                                    <Chat />
                                ) : (
                                    !conditions(ConditionName.isFullScreen) &&
                                    conditions(ConditionName.hasPlayList) &&
                                    conditions(ConditionName.showPlaylist) && <PlayList />
                                )}
                            </div>
                            {conditions(ConditionName.chatEnabled) && <ChatRules />}
                            {conditions(ConditionName.hasRecommendationList) && conditions(ConditionName.showRecommendationList) && (
                                <RecommendationList />
                            )}
                        </div>
                    )
                )}
            </div>
            <Popovers />
        </>
    );
};

export default VideoPlayer;
